import { Injectable } from "@angular/core"

export interface PlanItem {
    label: string
    included: boolean
    detail?: string
    tooltip?: string
}

export interface Plan {
    id: string
    name: string
    tagline: string
    color: string
    accent: string
    badge: string | null
    monthlyFee: number
    closingFeeLabel: string
    closingFeePct: number
    items: PlanItem[]
    cta: string
    estimate: string
    calcFn: (price: number) => number
}

export interface ComparisonRow {
    feature: string
    homeshark: string
    plus: string
    pro: string
}

export interface FaqItem {
    question: string
    answer: string
}

export const TRADITIONAL_PCT = 0.03
export const HOME_PRICE_MIN = 200_000
export const HOME_PRICE_MAX = 1_500_000
export const HOME_PRICE_DEFAULT = 450_000
export const HOME_PRICE_STEP = 10_000

@Injectable({ providedIn: "root" })
export class PricingService {
    readonly plans: Plan[] = [
        {
            id: "homeshark",
            name: "HomeShark",
            tagline: "Take full control of your home purchase.",
            color: "#004cbf",
            accent: "#26c8f5",
            badge: null,
            monthlyFee: 49,
            closingFeeLabel: "0.12% FMLS fee at closing",
            closingFeePct: 0.0012,
            items: [
                {
                    label: "Full MLS Search, All Tools & Dashboard",
                    included: true,
                },
                {
                    label: "Up to 3 Comp Its per month",
                    included: true,
                    tooltip:
                        "Comparable sale analysis for homes you're considering",
                },
                {
                    label: "Private Showings",
                    included: true,
                    detail: "$45–$60 per showing",
                },
                {
                    label: "GA Standard Offer (Contract Builder)",
                    included: true,
                    detail: "$499 per offer · terminations included",
                },
                {
                    label: "0.12% FMLS fee at closing",
                    included: true,
                    detail: "Paid directly to FMLS · Only applies to FMLS-listed homes; off-market / GAMLS-only listings are not charged this fee",
                },
                {
                    label: "Licensed Agent Chat Support",
                    included: true,
                    detail: "Limited hours",
                },
                {
                    label: "Offer Negotiations w/ Listing Agent",
                    included: false,
                },
                {
                    label: "Full Agent Team for Showings & Offers",
                    included: false,
                },
            ],
            cta: "Start with HomeShark",
            estimate: "Typical buyer: ~$1,444 + 0.12% of purchase price",
            calcFn: (price: number) => {
                const sub = 49 * 4
                const showings = 5 * 52.5
                const offers = 2 * 499
                const fmls = price * 0.0012
                return sub + showings + offers + fmls
            },
        },
        {
            id: "plus",
            name: "HomeShark Plus",
            tagline: "All the tools, plus AI-powered insights.",
            color: "#0062e8",
            accent: "#00b4f1",
            badge: "Most Popular",
            monthlyFee: 64,
            closingFeeLabel: "0.12% FMLS fee at closing",
            closingFeePct: 0.0012,
            items: [
                {
                    label: "Full MLS Search, All Tools & Dashboard",
                    included: true,
                },
                {
                    label: "Up to 5 Comp Its per month",
                    included: true,
                    tooltip:
                        "Comparable sale analysis for homes you're considering",
                },
                // { label: "Unlimited Comp Its", included: true },
                {
                    label: "AI Assisted Home Analysis",
                    included: true,
                    detail: "AI-powered insights on listings, pricing, and market trends",
                },
                {
                    label: "Private Showings",
                    included: true,
                    detail: "$45–$60 per showing",
                },
                {
                    label: "GA Standard Offer (Contract Builder)",
                    included: true,
                    detail: "$499 per offer · terminations included",
                },
                {
                    label: "0.12% FMLS fee at closing",
                    included: true,
                    detail: "Paid directly to FMLS · Only applies to FMLS-listed homes",
                },
                {
                    label: "Licensed Agent Chat Support",
                    included: true,
                    detail: "Limited hours",
                },
                {
                    label: "Offer Negotiations w/ Listing Agent",
                    included: false,
                },
                {
                    label: "Full Agent Team for Showings & Offers",
                    included: false,
                },
            ],
            cta: "Choose HomeShark Plus",
            estimate: "~$1,444 + 0.12% of purchase price + $64/mo subscription",
            calcFn: (price: number) => {
                const sub = 64 * 4
                const showings = 5 * 52.5
                const offers = 2 * 499
                const fmls = price * 0.0012
                return sub + showings + offers + fmls
            },
        },
        {
            id: "pro",
            name: "HomeShark, Agent Assisted",
            tagline: "A full team of sharks fighting for you.",
            color: "#002d80",
            accent: "#26c8f5",
            badge: "Full Service",
            monthlyFee: 149,
            closingFeeLabel: "1.5% at closing",
            closingFeePct: 0.015,
            items: [
                {
                    label: "Full MLS Search, All Tools & Dashboard",
                    included: true,
                },
                // {
                //     label: "Up to 5 Comp Its per month",
                //     included: true,
                //     tooltip:
                //         "Comparable sale analysis for homes you're considering",
                // },
                { label: "Unlimited Comp Its", included: true },
                {
                    label: "Private Showings",
                    included: true,
                    detail: "$45–$60/showing · up to 5 credited back at closing",
                },
                { label: "Unlimited Offers", included: true },
                { label: "Unlimited Agent Chat & Negotiation", included: true },
                { label: "Dedicated Buyer's Agent Team", included: true },
                { label: "White-Glove Transaction Management", included: true },
                {
                    label: "First Paid Offer Credited at Closing",
                    included: true,
                    detail: "If upgrading from HomeShark base or Plus plan",
                },
                {
                    label: "Exclusive Buyer Representation",
                    included: true,
                    detail: "Required at first showing",
                },
            ],
            cta: "Choose Agent Assisted",
            estimate:
                "1.5% of purchase price at closing + $149/mo subscription",
            calcFn: (price: number) => {
                const sub = 149 * 4
                const closing = price * 0.015
                return sub + closing
            },
        },
    ]

    readonly comparisonRows: ComparisonRow[] = [
        {
            feature: "Monthly Subscription",
            homeshark: "$49/mo",
            plus: "$64/mo",
            pro: "$149/mo",
        },
        {
            feature: "Closing Fee",
            homeshark: "0.12% FMLS fee only",
            plus: "0.12% FMLS fee only",
            pro: "1.5% of purchase price",
        },
        {
            feature: "Full MLS Search & Dashboard",
            homeshark: "✓",
            plus: "✓",
            pro: "✓",
        },
        {
            feature: "Comp Its (Comparable Analysis)",
            homeshark: "3 per month",
            plus: "Unlimited",
            pro: "Unlimited",
        },
        {
            feature: "AI Assisted Home Analysis",
            homeshark: "—",
            plus: "✓",
            pro: "✓",
        },
        {
            feature: "Private Showings",
            homeshark: "$45–$60 each",
            plus: "$45–$60 each",
            pro: "$45–$60 (up to 5 credited at closing)",
        },
        {
            feature: "Offer Builder (GA Standard)",
            homeshark: "$499 per offer",
            plus: "$499 per offer",
            pro: "Unlimited",
        },
        {
            feature: "Licensed Agent Chat",
            homeshark: "Limited hours",
            plus: "Limited hours",
            pro: "Unlimited",
        },
        { feature: "Negotiation Support", homeshark: "—", plus: "—", pro: "✓" },
        {
            feature: "Exclusive Buyer Representation",
            homeshark: "—",
            plus: "—",
            pro: "✓ (required at first showing)",
        },
        {
            feature: "Dedicated Agent Team",
            homeshark: "—",
            plus: "—",
            pro: "✓",
        },
        {
            feature: "First Offer Credit (if upgrading)",
            homeshark: "—",
            plus: "—",
            pro: "✓ $499 credited at closing",
        },
    ]

    readonly faqs: FaqItem[] = [
        {
            question: "What is the FMLS fee and why do I pay it?",
            answer: "FMLS (First Multiple Listing Service) is the MLS database that powers the majority of Georgia home listings. The 0.12% fee (purchase price × 0.0012) is paid directly to FMLS at closing in exchange for the listing access provided throughout your search. This fee is transparently passed through to you — HomeShark does not retain it. Important: this fee only applies if the home you purchase was listed on FMLS. Off-market sales and homes listed exclusively on GAMLS (Georgia MLS) are not subject to this fee.",
        },
        {
            question: "Can I switch plans partway through my search?",
            answer: "Absolutely.  You can always move up to a higher plan at any time.  If you write an offer on the HomeShark or Plus plan and then decide to upgrade to the Agent Assisted plan, the cost of that first offer ($499) will be credited back at closing. We encourage you to start with the HomeShark or Plus plan and only scale up to the Agent Assisted Plan if you feel you need more support.",
        },
        {
            question: 'What is a "Comp It" and do I really need one?',
            answer: "A Comp It is a comparable sales analysis — a professional review of recent nearby sold homes to help you understand what a home is truly worth before you make an offer. We strongly recommend running a Comp It before writing any offer. It's one of the most important tools to protect yourself in a negotiation.",
        },
        {
            question: "How do private showings work and what's the cost?",
            answer: "You schedule showings directly through your dashboard. The per-showing rate is individually determined and falls in the $45–$60 range. Agent Assisted buyers will have up to 5 showings credited back at closing.",
        },
        {
            question: "What does the HomeShark contract include?",
            answer: "The $499 offer fee covers a complete Georgia-standard offer package: the purchase and sale agreement, a financing contingency or cash sale addendum, and the lead paint exhibit. Terminations are included as needed at no additional cost. Repair Amendments will cost $149 and additional amendments will be priced based on complication.",
        },
        {
            question: "Does HomeShark represent me legally?",
            answer: "HomeShark is a licensed Georgia broker. When you sign up for HomeShark, Agent Assisted, you will sign an exclusive buyer representation agreement at the time of your first private showing. For the base HomeShark and Plus plans, you are acting as an empowered self-represented buyer with access to professional-grade tools and a licensed broker's contract services.",
        },
    ]

    getPlanById(id: string): Plan | undefined {
        return this.plans.find((p) => p.id === id)
    }

    calcSavings(planId: string, price: number): number {
        const plan = this.getPlanById(planId)
        if (!plan) return 0
        return price * TRADITIONAL_PCT - plan.calcFn(price)
    }
}
